import { AppstoreAddOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';

import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';

import { CustomHeader } from '../../GenericTable/CustomHeader';
import { GenericTable } from '../../GenericTable/GenericTable';
import {
  ImageTextCell,
  PriceCell,
  TagCell,
  TextCell,
} from '../../GenericTable/TableCellRenderers';
import { useTableColumns } from '../../GenericTable/useTableColumns';

export type AddOnConfiguration = {
  quantity: number;
  isPerPerson: boolean;
  isPerNight: boolean;
  isOptional: boolean;
};

export type AddOn = {
  id: string;
  name: string;
  images?: PictureView[];
  adultPrice?: number;
  childPrice?: number;
  areAdultsAllowed?: boolean;
  areChildrenAllowed?: boolean;
  maxQuantityPerBooking?: number;
  isOriginals: boolean;
  configuration?: AddOnConfiguration;
};

type AddOnsTableProps = {
  dataSource?: AddOn[];
  loading?: boolean;
  hideChildPriceColumn?: boolean;
};

export const AddOnsTable: React.FC<AddOnsTableProps> = ({
  dataSource = [],
  loading,
  hideChildPriceColumn = false,
}) => {
  const columnsConfig = useMemo(
    () => [
      {
        key: 'name',
        title: 'Activity name',
        render: (_: unknown, record: AddOn) => (
          <ImageTextCell images={record.images} title={record.name} />
        ),
      },
      {
        key: 'adultPrice',
        title: 'Adult',
        width: '140px',
        render: (value: number, record: AddOn) => {
          if (record.isOriginals) {
            return <TextCell value="Provided by Staycation" variant="grey" />;
          }

          if (record.areAdultsAllowed === false) {
            return <TextCell value="Not allowed" variant="grey" />;
          }

          return <PriceCell value={value} />;
        },
      },
      ...(hideChildPriceColumn
        ? []
        : [
            {
              key: 'childPrice',
              title: (
                <CustomHeader text="Child" tooltip="Child price (ages 2–12)" />
              ),
              width: '140px',
              render: (value: number, record: AddOn) => {
                if (record.isOriginals) {
                  return <TextCell value="-" variant="grey" />;
                }

                if (record.areChildrenAllowed === false) {
                  return <TextCell value="Not allowed" variant="grey" />;
                }

                if (value) {
                  return <PriceCell value={value} />;
                }

                if (record.configuration?.isPerPerson) {
                  return <PriceCell value={record.adultPrice} />;
                }

                return <TextCell value="-" variant="grey" />;
              },
            },
          ]),
      {
        key: 'maxQuantityPerBooking',
        title: 'Max. quantity per booking',
        width: '325px',
        render: (_: unknown, record: AddOn) => {
          const value = record.maxQuantityPerBooking;

          if (!value) {
            return <span>-</span>;
          }

          return <TagCell values={[value, 'per booking']} />;
        },
      },
    ],
    [hideChildPriceColumn]
  );

  const columns = useTableColumns(columnsConfig);

  return (
    <GenericTable
      title="Extras"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      emptyState={{
        title: 'No extras',
        description:
          'This package has no extras available. Contact your Account Manager for more information.',
        icon: <AppstoreAddOutlined />,
      }}
    />
  );
};
